import { useQuery } from '@tanstack/react-query';
import { apiGet, type ApiError } from '../lib/api';
import { AUDITED_READ } from '../lib/query-client';

/** Only these three timeline event types carry readable content. */
export type ContentEventType = 'reflection' | 'check_in' | 'chat_message';

export interface ContentBlock {
  id: string;
  heading: string;
  body: string;
  deleted: boolean;
}

interface Options {
  userId: string;
  eventType: ContentEventType;
  resourceId: string;
  // `| undefined` is explicit because apps/admin runs with
  // exactOptionalPropertyTypes: passing an absent conversationId through as
  // `undefined` is otherwise a type error at the call site.
  conversationId?: string | undefined;
  reason: string;
  /** FALSE until a non-empty reason is committed. This flag IS the privacy control. */
  enabled: boolean;
}

interface ReflectionRow {
  id: string;
  content: string;
  input_mode: string;
  created_at: string;
  deleted_at: string | null;
}
interface MessageRow {
  id: string;
  role: string;
  content: string;
  created_at: string;
}
interface CheckInRow {
  id: string;
  local_day: string;
  note: string | null;
  created_at: string;
  deleted_at: string | null;
  selections: { kind: 'frame' | 'compass'; frame: string | null; word: string }[];
}

function when(iso: string) {
  return new Date(iso).toLocaleString();
}

/**
 * Normalizes all three surfaces to one block shape so ContentModal renders a single
 * way. The endpoint choice is derived from the event type — the modal never builds
 * a URL itself.
 */
export function useContentQuery(opts: Options) {
  const { userId, eventType, resourceId, conversationId, reason, enabled } = opts;

  return useQuery<{ blocks: ContentBlock[] }, ApiError>({
    // The reason is part of the key: a different reason is a different audited read,
    // not a cache hit on the previous one.
    queryKey: ['admin', 'content', userId, eventType, resourceId, reason],
    enabled,
    ...AUDITED_READ,
    queryFn: async () => {
      if (eventType === 'chat_message') {
        const res = (await apiGet(
          `/admin/users/${userId}/transcript` as '/admin/users/{id}/transcript',
          { conversation_id: conversationId, reason, limit: 200 },
        )) as { messages: MessageRow[] };
        return {
          blocks: res.messages.map((m) => ({
            id: m.id,
            heading: `${m.role} · ${when(m.created_at)}`,
            body: m.content,
            deleted: false,
          })),
        };
      }

      if (eventType === 'reflection') {
        const res = (await apiGet(
          `/admin/users/${userId}/reflections` as '/admin/users/{id}/reflections',
          { reason, limit: 200 },
        )) as { reflections: ReflectionRow[] };
        // The endpoint pages the whole surface; the modal shows the one event that
        // was clicked, falling back to everything if the id is not on this page.
        const rows = res.reflections.filter((r) => r.id === resourceId);
        return {
          blocks: (rows.length > 0 ? rows : res.reflections).map((r) => ({
            id: r.id,
            heading: `${when(r.created_at)} · ${r.input_mode}`,
            body: r.content,
            deleted: r.deleted_at !== null,
          })),
        };
      }

      const res = (await apiGet(
        `/admin/users/${userId}/check-ins` as '/admin/users/{id}/check-ins',
        { reason, limit: 200 },
      )) as { check_ins: CheckInRow[] };
      const rows = res.check_ins.filter((c) => c.id === resourceId);
      return {
        blocks: (rows.length > 0 ? rows : res.check_ins).map((c) => ({
          id: c.id,
          heading: `${c.local_day} · ${c.selections.map((s) => s.word).join(', ')}`,
          body: c.note ?? '(no note)',
          deleted: c.deleted_at !== null,
        })),
      };
    },
  });
}
